import React from 'react';
import { Split } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import type {
  WorkflowNodeRenderStrategy,
  WorkflowNodeViewData,
} from '../types';
import { asRecord } from '../../utils';
import { describeFieldPath } from './describe-field-path';

const { WorkflowNodeType, parseGroupByConfig } = PersistedWorkflow;

/**
 * `One group per /user_id` — the field, because that is the only decision this
 * node makes and the one thing worth reading from the canvas.
 *
 * The list path is left off deliberately. It is `/items` on essentially every
 * group-by, since a bucket flush is what these read, so printing it would spend
 * the card's one line on the part nobody varies.
 */
function summarize(node: WorkflowNodeViewData): string | undefined {
  const parsed = parseGroupByConfig(asRecord(node.data));
  if (!parsed.ok) {
    return undefined;
  }
  return `One group per ${describeFieldPath(parsed.config.keyPath, new Map())}`;
}

export const groupByNodeStrategy: WorkflowNodeRenderStrategy = {
  nodeType: WorkflowNodeType.GroupBy,
  accent: 'control',
  badge: 'Group by',
  getTitle: () => 'Group by',
  getDescription: () =>
    'Split a list into groups by a field. Add a For each to run per group.',
  renderIcon: () => <Split className="h-4 w-4" />,
  renderBody: (node) => (
    <Text.Caption className="text-muted-foreground block truncate">
      {summarize(node) ?? 'No field to group on'}
    </Text.Caption>
  ),
};
